'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { apiConfig } from '@/config/apiConfig';

import type { NotificationResponse } from '@/services/notificationApi';

const MAX_RECONNECT_ATTEMPTS = 5;

export type NotificationSocketStatus = 'idle' | 'connected' | 'failed';

/**
 * Push channel for the notifications drawer (see notification-svc's
 * infra/websocket.yaml). Auth is the browser's own `session` cookie — API
 * Gateway forwards it on the WebSocket handshake same as any same-origin
 * request, so no query-param token is needed here (matches how
 * notification-svc's $connect handler authenticates).
 *
 * Reconnect/backoff follows the same convention as the anonymous-visitor
 * socket in AudienceConfiguration.tsx: exponential backoff capped at 30s,
 * close code 1000 means intentional (no reconnect).
 */
export function useNotificationSocket(
  enabled: boolean,
  onMessage: (notification: NotificationResponse) => void
): NotificationSocketStatus {
  const [status, setStatus] = useState<NotificationSocketStatus>('idle');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    // Disabled for now — notification-ws isn't resolvable in this
    // environment (ERR_NAME_NOT_RESOLVED against staging). Going straight to
    // 'failed' routes notifications through the existing REST polling
    // fallback in useNotifications.ts instead of spamming failed connection
    // attempts. Re-enable by restoring the WebSocket setup below.
    setStatus('failed');
    return;

    /* eslint-disable no-unreachable */
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(apiConfig.baseUrls.notificationWs);

      ws.onopen = () => {
        setStatus('connected');
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const notification: NotificationResponse = JSON.parse(event.data);
          onMessageRef.current(notification);
        } catch (err) {
          console.error('[useNotificationSocket] Failed to parse pushed notification', err);
        }
      };

      ws.onclose = (event) => {
        wsRef.current = null;
        if (event.code === 1000) {
          setStatus('idle');
          return;
        }
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 30000);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        } else {
          setStatus('failed');
        }
      };

      ws.onerror = () => {
        // onclose fires right after with a non-1000 code and drives the
        // reconnect/failed transition above — nothing extra needed here.
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('[useNotificationSocket] Failed to open WebSocket', err);
      setStatus('failed');
    }
    /* eslint-enable no-unreachable */
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Disabled');
      wsRef.current = null;
    }
    setStatus('idle');
  }, []);

  useEffect(() => {
    if (!enabled) {
      disconnect();
      return;
    }
    reconnectAttemptsRef.current = 0;
    connect();
    return disconnect;
  }, [enabled, connect, disconnect]);

  return status;
}
